import { normalizeWidgetParams } from "@shared/utils/urls/widget-params";
import { WidgetQueryParams } from "@app-contexts/sdk/services/Widget";

import {
  checkoutWidget,
  getCleanPageUrl,
  SDK_PARAMS_STORAGE_KEY,
} from "./checkout";
import {
  omitCustomerAttributionToken,
  stripCustomerAttributionTokenFromUrl,
} from "./customer-attribution";

type AffirmRedirectParams = WidgetQueryParams & {
  payment_intent: string;
  payment_intent_client_secret: string;
  affirm: string;
  redirect_status?: string;
  stripe?: string;
};

type AlipayRedirectParams = WidgetQueryParams & {
  alipay: string;
  redirect_status?: string;
  stripe?: string;
  payment_intent?: string;
  payment_intent_client_secret?: string;
};

type CardRedirectParams = WidgetQueryParams & {
  payment_intent: string;
  payment_intent_client_secret: string;
  redirect_status?: string;
  stripe: string;
};

type SetupIntentRedirectParams = WidgetQueryParams & {
  setup_intent: string;
  setup_intent_client_secret: string;
  redirect_status?: string;
  stripe: string;
};

type PaymentRedirectType = "affirm" | "alipay" | "card" | "setup";

type PaymentRedirectParams =
  | AffirmRedirectParams
  | AlipayRedirectParams
  | CardRedirectParams
  | SetupIntentRedirectParams;

function getPaymentRedirectType(
  urlParams: URLSearchParams
): PaymentRedirectType | null {
  if (urlParams.get("affirm") === "true") return "affirm";
  if (urlParams.get("alipay") === "true") return "alipay";
  if (
    urlParams.get("stripe") === "true" &&
    (urlParams.has("setup_intent") ||
      urlParams.has("setup_intent_client_secret"))
  ) {
    return "setup";
  }
  if (urlParams.get("stripe") === "true") return "card";
  return null;
}

function getAffirmReturnParams(
  urlParams: URLSearchParams
): AffirmRedirectParams | null {
  const paymentIntent = urlParams.get("payment_intent");
  const clientSecret = urlParams.get("payment_intent_client_secret");
  const affirm = urlParams.get("affirm");
  const redirectStatus = urlParams.get("redirect_status");
  const stripe = urlParams.get("stripe");

  if (affirm !== "true" || !paymentIntent || !clientSecret) return null;

  return {
    payment_intent: paymentIntent,
    payment_intent_client_secret: clientSecret,
    affirm,
    ...(redirectStatus && { redirect_status: redirectStatus }),
    ...(stripe && { stripe }),
  };
}

function getAlipayReturnParams(
  urlParams: URLSearchParams
): AlipayRedirectParams | null {
  const alipay = urlParams.get("alipay");
  const redirectStatus = urlParams.get("redirect_status");
  const stripe = urlParams.get("stripe");
  const paymentIntent = urlParams.get("payment_intent");
  const clientSecret = urlParams.get("payment_intent_client_secret");

  if (alipay !== "true") return null;

  return {
    alipay,
    ...(redirectStatus && { redirect_status: redirectStatus }),
    ...(stripe && { stripe }),
    ...(paymentIntent && { payment_intent: paymentIntent }),
    ...(clientSecret && { payment_intent_client_secret: clientSecret }),
  };
}

function getCardReturnParams(
  urlParams: URLSearchParams
): CardRedirectParams | null {
  const stripe = urlParams.get("stripe");
  const paymentIntent = urlParams.get("payment_intent");
  const clientSecret = urlParams.get("payment_intent_client_secret");
  const redirectStatus = urlParams.get("redirect_status");

  if (stripe !== "true" || !paymentIntent || !clientSecret) return null;

  return {
    stripe,
    payment_intent: paymentIntent,
    payment_intent_client_secret: clientSecret,
    ...(redirectStatus && { redirect_status: redirectStatus }),
  };
}

function getSetupIntentReturnParams(
  urlParams: URLSearchParams
): SetupIntentRedirectParams | null {
  const stripe = urlParams.get("stripe");
  const setupIntent = urlParams.get("setup_intent");
  const clientSecret = urlParams.get("setup_intent_client_secret");
  const redirectStatus = urlParams.get("redirect_status");

  if (stripe !== "true" || !setupIntent || !clientSecret) return null;

  return {
    stripe,
    setup_intent: setupIntent,
    setup_intent_client_secret: clientSecret,
    ...(redirectStatus && { redirect_status: redirectStatus }),
  };
}

function getSavedWidgetParams(): WidgetQueryParams {
  try {
    const stored = sessionStorage.getItem(SDK_PARAMS_STORAGE_KEY);
    if (stored) {
      sessionStorage.removeItem(SDK_PARAMS_STORAGE_KEY);
      const savedParams = normalizeWidgetParams(
        omitCustomerAttributionToken(JSON.parse(stored) as WidgetQueryParams)
      );
      return savedParams.redirect_url
        ? {
            ...savedParams,
            redirect_url: stripCustomerAttributionTokenFromUrl(
              savedParams.redirect_url
            ),
          }
        : savedParams;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return {};
}

function openCheckoutWithRedirectParams(
  redirectParams: PaymentRedirectParams,
  cleanPageUrl: string
): void {
  const savedParams = getSavedWidgetParams();

  checkoutWidget({
    ...savedParams,
    ...redirectParams,
    redirect_url: encodeURIComponent(cleanPageUrl),
  });
}

/**
 * Detects off-site Stripe payment redirect params in the current URL and
 * automatically re-opens the checkout widget to complete the payment.
 *
 * Stripe/Affirm redirects back to the host page with:
 *   ?affirm=true&payment_intent=pi_xxx&payment_intent_client_secret=pi_xxx_secret
 *
 * Stripe/Alipay can redirect back with:
 *   ?stripe=true&alipay=true
 *
 * Generic Stripe card confirmations can redirect back with:
 *   ?stripe=true&payment_intent=pi_xxx&payment_intent_client_secret=pi_xxx_secret
 *
 * Deferred card confirmations can redirect back with:
 *   ?stripe=true&setup_intent=seti_xxx&setup_intent_client_secret=seti_xxx_secret
 *
 * This handler forwards those params into the checkout iframe so
 * PaymentManagerContext can confirm the payment.
 */
export function handlePaymentRedirect(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectType = getPaymentRedirectType(urlParams);
  if (!redirectType) return;

  let redirectParams: PaymentRedirectParams | null = null;
  if (redirectType === "affirm") {
    redirectParams = getAffirmReturnParams(urlParams);
  } else if (redirectType === "alipay") {
    redirectParams = getAlipayReturnParams(urlParams);
  } else if (redirectType === "card") {
    redirectParams = getCardReturnParams(urlParams);
  } else if (redirectType === "setup") {
    redirectParams = getSetupIntentReturnParams(urlParams);
  }
  if (!redirectParams) return;

  const cleanPageUrl = getCleanPageUrl();
  window.history.replaceState({}, "", cleanPageUrl);

  const openCheckoutWithParams = (): void => {
    openCheckoutWithRedirectParams(redirectParams, cleanPageUrl);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", openCheckoutWithParams);
  } else {
    openCheckoutWithParams();
  }
}
